import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFormStore, usePaymentStore, useTransactionStore, useUIStore, PaymentSession, PaymentStatus } from '../stores';
import { useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitProvider } from '@reown/appkit/react';
import { useAppKitWallet } from '@reown/appkit-wallet-button/react';
import '../config/appkit'; // Ensure AppKit is ready before the widget renders
import NetworkSelector from './NetworkSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import QRPayment from './QRPayment';
import { getNetworkConfig } from '../config/networks';
import { validateAmount, sanitizeInput, validateNetwork, validateToken } from '../utils/address-validation';
import { trackPaymentEvent } from '../utils/analytics';
import { sendWalletPayment, WalletPaymentError } from '../utils/wallet-payment';
import { handleRadomWalletPayment, supportsDirectWalletPayment } from '../utils/handleRadomWalletPayment';
// Phantom wallet support removed - EVM chains only

const EVM_NETWORKS = new Set(['Ethereum', 'Arbitrum', 'Base', 'Polygon', 'BNB', 'Avalanche']);

interface AddEthereumChainParameter {
  chainId: `0x${string}`;
  chainName: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  rpcUrls: string[];
  blockExplorerUrls?: string[];
}

const FALLBACK_RPC_URLS: Record<number, string> = {
  1: import.meta.env.VITE_ETHEREUM_RPC_URL || 'https://rpc.ankr.com/eth',
  42161: import.meta.env.VITE_ARBITRUM_RPC_URL || 'https://rpc.ankr.com/arbitrum',
  8453: import.meta.env.VITE_BASE_RPC_URL || 'https://rpc.ankr.com/base',
  137: import.meta.env.VITE_POLYGON_RPC_URL || 'https://rpc.ankr.com/polygon',
  56: import.meta.env.VITE_BNB_RPC_URL || 'https://rpc.ankr.com/bsc',
  43114: import.meta.env.VITE_AVALANCHE_RPC_URL || 'https://rpc.ankr.com/avalanche'
};

const toHexChainId = (chainId: number): `0x${string}` => `0x${chainId.toString(16)}` as `0x${string}`;

const EVM_CHAIN_PARAMETERS: Record<number, AddEthereumChainParameter> = {
  1: {
    chainId: toHexChainId(1),
    chainName: 'Ethereum',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: [FALLBACK_RPC_URLS[1]],
    blockExplorerUrls: ['https://etherscan.io']
  },
  42161: {
    chainId: toHexChainId(42161),
    chainName: 'Arbitrum One',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: [FALLBACK_RPC_URLS[42161]],
    blockExplorerUrls: ['https://arbiscan.io']
  },
  8453: {
    chainId: toHexChainId(8453),
    chainName: 'Base',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: [FALLBACK_RPC_URLS[8453]],
    blockExplorerUrls: ['https://basescan.org']
  },
  137: {
    chainId: toHexChainId(137),
    chainName: 'Polygon',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrls: [FALLBACK_RPC_URLS[137]],
    blockExplorerUrls: ['https://polygonscan.com']
  },
  56: {
    chainId: toHexChainId(56),
    chainName: 'BNB Chain',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls: [FALLBACK_RPC_URLS[56]],
    blockExplorerUrls: ['https://bscscan.com']
  },
  43114: {
    chainId: toHexChainId(43114),
    chainName: 'Avalanche C-Chain',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    rpcUrls: [FALLBACK_RPC_URLS[43114]],
    blockExplorerUrls: ['https://snowtrace.io']
  }
};

const normalizeChainId = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('0x')) {
      const parsed = parseInt(trimmed, 16);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    const parsed = parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

interface PaymentWidgetProps {
  walletAddress: string;
  pricePerToken: number;
  currentPhase: number;
  isActive: boolean;
  onSuccess: () => void;
}


const PaymentWidget: React.FC<PaymentWidgetProps> = ({
  walletAddress,
  pricePerToken,
  currentPhase,
  isActive,
  onSuccess
}) => {
  // Use Zustand stores instead of useState
  const formStore = useFormStore();
  const paymentStore = usePaymentStore();
  const transactionStore = useTransactionStore();
  const uiStore = useUIStore();

  // Extract state from stores
  const {
    amountUsd,
    selectedNetwork,
    selectedToken,
    setAmount,
    setNetwork,
    setToken,
    getIsValidAmount,
    getTokensToReceive
  } = formStore;

  // No need to update price per token in store - it's passed as a parameter to getTokensToReceive

  const {
    paymentSession,
    paymentStatus,
    isSessionExpired,
    timeUntilExpiry,
    isCreatingSession,
    isPolling,
    pollRetryCount,
    createPaymentSession,
    checkPaymentStatus,
    setPaymentSession,
    setPaymentStatus,
    setSessionExpired,
    setTimeUntilExpiry,
    setCreatingSession,
    setPolling,
    incrementPollRetryCount,
    resetPollRetryCount,
    resetPayment
  } = paymentStore;

  const {
    txStatus,
    txHash,
    txError,
    pendingPaymentAfterWalletConnect,
    showQR,
    setTxStatus,
    setTxHash,
    setTxError,
    setPendingPaymentAfterWalletConnect,
    setShowQR,
    updateTransactionState,
    resetTransaction
  } = transactionStore;

  const {
    loading,
    error,
    copySuccess,
    isCopying,
    isRateLimited,
    setLoading,
    setError,
    setCopySuccess,
    setIsCopying,
    updateLastClickTime,
    copyToClipboard,
    clearError,
    resetUI
  } = uiStore;

  // Standardized error handling utility
  const handleError = useCallback((error: unknown, context: string, fallbackMessage?: string) => {
    console.error(`${context}:`, error);

    if (error instanceof Error) {
      setError(error.message);
    } else if (typeof error === 'string') {
      setError(error);
    } else {
      setError(fallbackMessage || `An error occurred during ${context.toLowerCase()}`);
    }
  }, [setError]);

  // Use store's batch update method
  const updateTxState = useCallback((updates: Partial<{
    txStatus: typeof txStatus;
    txError: string;
    txHash: string;
  }>) => {
    updateTransactionState(updates);
  }, [updateTransactionState]);

  // Use store's validation
  const handleAmountChange = useCallback((value: string) => {
    setAmount(value);
    if (error && error.includes('amount')) {
      clearError();
    }
  }, [setAmount, error, clearError]);

  // Get tokens to receive from store (computed function)
  const tokensToReceive = formStore.getTokensToReceive(pricePerToken);

  // Form change handlers
  const handleNetworkChange = useCallback((network: string) => {
    setNetwork(network);
    if (error && error.includes('network')) {
      clearError();
    }
  }, [setNetwork, error, clearError]);

  const handleTokenChange = useCallback((token?: string) => {
    setToken(token);
    if (error && error.includes('token')) {
      clearError();
    }
  }, [setToken, error, clearError]);


  // Real-time validation for amount input (debounced to prevent infinite loops)
  useEffect(() => {
    if (amountUsd && amountUsd.trim()) {
      const amountValidation = validateAmount(amountUsd);
      if (!amountValidation.isValid) {
        setError(amountValidation.error || 'Invalid amount');
      } else {
        // Only clear error if it's an amount-related error
        if (error && (error.includes('amount') || error.includes('Invalid amount'))) {
          setError(null);
        }
      }
    } else {
      // Only clear error if it's an amount-related error
      if (error && (error.includes('amount') || error.includes('Invalid amount'))) {
        setError(null);
      }
    }
  }, [amountUsd, error]);

  // Validate network and token on component mount and when they change
  useEffect(() => {
    const networkValidation = validateNetwork(selectedNetwork);
    if (!networkValidation.isValid) {
      setError(networkValidation.error || 'Invalid network');
      return;
    }

    if (selectedToken) {
      const tokenValidation = validateToken(selectedToken, selectedNetwork);
      if (!tokenValidation.isValid) {
        setError(tokenValidation.error || 'Invalid token');
        return;
      }
    }

    // Clear any previous validation errors if everything is valid
    if (error && (error.includes('network') || error.includes('token') ||
                  error.includes('Invalid network') || error.includes('Invalid token'))) {
      setError(null);
    }
  }, [selectedNetwork, selectedToken, error]);

  // Track widget opened on mount
  useEffect(() => {
    trackPaymentEvent('payment_widget_opened', {
      initial_network: selectedNetwork
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Call onSuccess when payment is completed
  useEffect(() => {
    if (paymentStatus && ['completed', 'completed_success'].includes(paymentStatus.status)) {
      onSuccess();
    }
  }, [paymentStatus, onSuccess]);
  
  // AppKit hooks - much simpler!
  const { open } = useAppKit();
  const { address: connectedAddress, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider('eip155'); // EVM namespace
  
  // Simple wallet connection check - AppKit handles everything
  const isWalletConnected = useCallback(() => {
    return isConnected && !!connectedAddress;
  }, [isConnected, connectedAddress]);

  // Payment session trigger - moved here to avoid hoisting issues
  const triggerCreatePaymentSession = useCallback(async () => {
    const parsedAmount = parseFloat(amountUsd);
    const actualWalletAddress = connectedAddress || walletAddress;

    if (!actualWalletAddress) {
      throw new Error('Wallet address is required before starting a payment session.');
    }

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      throw new Error('Enter a valid payment amount before starting a session.');
    }

    const payload = {
      walletAddress: actualWalletAddress,
      amountUsd: parsedAmount,
      network: selectedNetwork,
      token: selectedToken
    };
    
    await createPaymentSession(payload);
  }, [connectedAddress, walletAddress, amountUsd, selectedNetwork, selectedToken, createPaymentSession]);

  const ensureCorrectNetwork = useCallback(async (targetChainId: number) => {
    if (!walletProvider || typeof walletProvider.request !== 'function') {
      throw new WalletPaymentError('Connected wallet does not expose an EIP-1193 request method');
    }

    const currentChainId = normalizeChainId(chainId);
    if (currentChainId === targetChainId) {
      return;
    }

    const targetHex = toHexChainId(targetChainId);

    try {
      await walletProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: targetHex }]
      });
      return;
    } catch (switchError: any) {
      console.warn('[Wallet] Failed to switch chain, attempting addChain fallback', switchError);

      if (switchError?.code === 4001) {
        throw new WalletPaymentError('User rejected network switch', 4001);
      }

      if (switchError?.code === 4902) {
        const addChainParams = EVM_CHAIN_PARAMETERS[targetChainId];
        if (!addChainParams) {
          throw new WalletPaymentError(`Chain ${targetChainId} is not supported`, 4902);
        }

        try {
          await walletProvider.request({
            method: 'wallet_addEthereumChain',
            params: [addChainParams]
          });

          await walletProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: targetHex }]
          });
          return;
        } catch (addError: any) {
          if (addError?.code === 4001) {
            throw new WalletPaymentError('User rejected adding the requested network', 4001);
          }

          throw new WalletPaymentError(
            addError?.message || 'Failed to add requested network',
            addError?.code ?? 4902
          );
        }
      }

      throw new WalletPaymentError(
        switchError?.message || 'Failed to switch to requested network',
        switchError?.code
      );
    }
  }, [walletProvider, chainId]);

  // Enhanced wallet payment using new backend-driven system
  const handleSimpleWalletPayment = useCallback(async () => {
    console.log('🔍 Enhanced wallet payment called with:', {
      amountUsd,
      selectedNetwork,
      selectedToken,
      hasConnectedAddress: !!connectedAddress,
      hasWalletProvider: !!walletProvider,
      supportsDirectPayment: supportsDirectWalletPayment(selectedNetwork, selectedToken),
      isConnected,
      address
    });

    if (!connectedAddress || !amountUsd || !selectedNetwork) {
      console.error('❌ Missing requirements for wallet payment:', {
        hasConnectedAddress: !!connectedAddress,
        amountUsd,
        selectedNetwork
      });
      return;
    }

    if (!EVM_NETWORKS.has(selectedNetwork)) {
      console.log('Non-EVM network, skipping wallet payment:', selectedNetwork);
      return;
    }

    try {
      console.log('🚀 Starting backend-driven wallet payment');
      setTxError('');
      setTxHash('');
      setTxStatus('preparing');

      // Use new backend-driven payment flow
      const result = await handleRadomWalletPayment(
        {
          amount: amountUsd,
          currency: selectedToken || 'ETH',
          walletAddress: connectedAddress,
          network: selectedNetwork
        },
        {
          onSwitching: () => {
            console.log('🔄 Switching networks...');
            setTxStatus('preparing');
          },
          onAwaitingApproval: () => {
            console.log('⏳ Awaiting token approval...');
            setTxStatus('awaiting-signature');
          },
          onAwaitingSignature: () => {
            console.log('✍️ Awaiting signature...');
            setTxStatus('awaiting-signature');
          },
          onBroadcasting: () => {
            console.log('📡 Broadcasting transaction...');
            setTxStatus('broadcasting');
          },
          onSuccess: (txHash: string) => {
            console.log('✅ Payment successful:', txHash);
            setTxHash(txHash);
            setTxStatus('success');
            
            // Track successful payment
            trackPaymentEvent('payment_success', {
              network: selectedNetwork,
              amount_usd: parseFloat(amountUsd),
              payment_method: 'direct_wallet',
              token: selectedToken || 'native',
              tx_hash: txHash
            });
          },
          onError: (error: Error) => {
            console.error('❌ Payment error:', error);
            let displayMessage = 'Transaction failed. Please try again.';
            
            if (error.message.includes('rejected')) {
              displayMessage = 'Transaction rejected by user';
            } else if (error.message.includes('insufficient funds')) {
              displayMessage = 'Insufficient funds for transaction';
            } else if (error.message.includes('network')) {
              displayMessage = 'Please switch to the correct network in your wallet';
            } else {
              displayMessage = error.message;
            }
            
            setTxError(displayMessage);
            setTxStatus('error');
          }
        }
      );

      console.log('✅ Backend-driven payment completed:', result);

    } catch (error) {
      console.error('❌ Backend-driven payment failed:', error);
      
      // Graceful fallback: show QR payment when direct wallet payment fails
      console.log('🔄 Falling back to traditional QR payment flow');
      
      let displayMessage = 'Direct wallet payment failed. You can still complete payment via QR code.';

      if (error instanceof Error) {
        if (error.message.includes('rejected')) {
          displayMessage = 'Transaction rejected. You can try again or use QR payment.';
        } else if (error.message.includes('insufficient funds')) {
          displayMessage = 'Insufficient funds for transaction';
        } else if (error.message.includes('network')) {
          displayMessage = 'Please switch to the correct network in your wallet';
        } else {
          displayMessage = error.message;
        }
      }

      setTxError(displayMessage);
      setTxStatus('error');
      
      // Don't clear the payment session on error - user can still use QR payment
      // The payment session remains valid and QR payment will still work
    }
  }, [amountUsd, selectedNetwork, selectedToken, connectedAddress, walletProvider, setTxError, setTxHash, setTxStatus]);

  // checkPaymentStatus is now handled by the paymentStore

  // Polling is now handled by the paymentStore

  // Check session expiration
  useEffect(() => {
    let interval: number | undefined;

    if (paymentSession && paymentSession.expiresAt) {
      const checkExpiration = () => {
        const now = Date.now();
        const expiryTime = new Date(paymentSession.expiresAt).getTime();
        const timeLeft = expiryTime - now;

        if (timeLeft <= 0) {
          setSessionExpired(true);
          setTimeUntilExpiry(0);
          setPolling(false); // Stop polling
          setError('Payment session has expired. Please create a new payment session.');
          trackPaymentEvent('session_expired', {
            session_id: paymentSession.sessionId,
            network: paymentSession.network
          });
        } else {
          setTimeUntilExpiry(Math.floor(timeLeft / 1000)); // Convert to seconds
          setSessionExpired(false);
        }
      };

      // Check immediately
      checkExpiration();

      // Check every second
      interval = setInterval(checkExpiration, 1000) as unknown as number;
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [paymentSession]);

  // Auto-trigger wallet payment when session is created and wallet is connected
  useEffect(() => {
    const walletConnected = isWalletConnected();

    if (paymentSession && walletConnected && txStatus === 'idle' && !isSessionExpired) {
      // Prevent multiple concurrent payment attempts
      if (txStatus === 'idle') {
        handleSimpleWalletPayment();
      }
    }
  }, [paymentSession, txStatus, isSessionExpired, selectedNetwork, isConnected, connectedAddress, handleSimpleWalletPayment, isWalletConnected]);

  // Auto-create payment session when wallet connects (for users who clicked Buy Now while disconnected)
  useEffect(() => {
    if (isWalletConnected() && !paymentSession && !loading && !isCreatingSession &&
        amountUsd && !isNaN(parseFloat(amountUsd)) && parseFloat(amountUsd) > 0 &&
        pendingPaymentAfterWalletConnect) {
      // User clicked Buy Now while disconnected, now wallet is connected - create payment session
      setPendingPaymentAfterWalletConnect(false);
      triggerCreatePaymentSession()
        .catch((err) => {
          handleError(err, 'Creating payment session', 'Failed to create payment session. Please try again.');
          setLoading(false);
        });
    }
  }, [paymentSession, loading, amountUsd, pendingPaymentAfterWalletConnect, isCreatingSession, selectedNetwork, isConnected, connectedAddress, triggerCreatePaymentSession, handleError, setLoading]);

  // Format time remaining for display
  const formatTimeRemaining = (seconds: number): string => {
    if (seconds <= 0) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  // createPaymentSession is now handled by the paymentStore

  const handleBuyNow = useCallback(async () => {
    console.log('🔴 Buy button clicked');
    console.log('🔍 Debug state:', {
      isRateLimited,
      amountUsd,
      selectedNetwork,
      selectedToken,
      isValidAmount: getIsValidAmount(),
      isWalletConnected: isWalletConnected(),
      isConnected,
      connectedAddress,
      chainId,
      walletAddress
    });

    // Rate limiting: Prevent spam clicks (minimum 1 second delay)
    if (isRateLimited) {
      console.log('🔴 Rate limited - returning early');
      return; // Silently ignore rapid clicks
    }
    updateLastClickTime();

    // Validate amount input
    if (!getIsValidAmount()) {
      console.log('🔴 Invalid amount');
      setError('Please enter a valid amount between $0.01 and $10,000');
      return;
    }

    // Validate network selection
    const networkValidation = validateNetwork(selectedNetwork);
    if (!networkValidation.isValid) {
      console.log('🔴 Invalid network:', networkValidation);
      setError(networkValidation.error || 'Invalid network');
      return;
    }

    // Validate token selection
    if (selectedToken) {
      const tokenValidation = validateToken(selectedToken, selectedNetwork);
      if (!tokenValidation.isValid) {
        console.log('🔴 Invalid token:', tokenValidation);
        setError(tokenValidation.error || 'Invalid token');
        return;
      }
    }

    console.log('✅ All validations passed, setting loading=true');
    setLoading(true);
    setError(null);

    // Networks without wallet support should create session immediately
    const noWalletNetworks = ['Tron', 'Polkadot', 'Bitcoin'];
    if (noWalletNetworks.includes(selectedNetwork)) {
      console.log('🔵 No wallet network detected, creating session immediately');
      try {
        await triggerCreatePaymentSession();
      } catch (err) {
        console.log('🔴 Error creating payment session for no-wallet network:', err);
        handleError(err, 'Creating payment session', 'Failed to create payment session. Please try again.');
        setLoading(false);
      }
      return;
    }

    // If wallet is connected, create payment session immediately
    if (isWalletConnected()) {
      console.log('✅ Wallet is connected, creating payment session');
      try {
        await triggerCreatePaymentSession();
      } catch (err) {
        console.log('🔴 Error creating payment session for connected wallet:', err);
        handleError(err, 'Creating payment session', 'Failed to create payment session. Please try again.');
        setLoading(false);
      }
    } else {
      // If wallet is not connected, open wallet modal
      console.log('🔵 Wallet not connected, opening wallet modal');
      try {
        setPendingPaymentAfterWalletConnect(true);
        await open();
        console.log('✅ Wallet modal opened successfully');
        // Note: The actual payment creation will happen after wallet connects
        // This is handled by the useEffect that watches for wallet connection changes
      } catch (err) {
        console.log('🔴 Error opening wallet modal:', err);
        handleError(err, 'Opening wallet modal', 'Failed to connect wallet. Please try again.');
        setLoading(false);
        setPendingPaymentAfterWalletConnect(false);
      }
    }
  }, [amountUsd, selectedNetwork, selectedToken, walletAddress, pricePerToken]); // Removed function dependencies to prevent unnecessary re-renders

  // checkPaymentStatus moved above

  // resetPayment is now handled by the paymentStore

  // handleWalletPayment is now handled by the transactionStore

  // Track explicit QR open via button
  useEffect(() => {
    if (showQR && paymentSession) {
      trackPaymentEvent('qr_fallback_shown', {
        session_id: paymentSession.sessionId,
        network: paymentSession.network
      });
    }
  }, [showQR, paymentSession]);

  // Using copyToClipboard from UI store

  // Input sanitization utility with security improvements
  const sanitizeAmountInput = useCallback((value: string): string => {
    if (!value || typeof value !== 'string') return '';

    // Limit input length to prevent DoS attacks
    const trimmed = value.trim();
    if (trimmed.length > 20) {
      return trimmed.substring(0, 20);
    }

    // Remove scientific notation, exponential notation, and other dangerous patterns
    let sanitized = trimmed
      .replace(/[eE][+-]?\d+/g, '') // Remove scientific notation
      .replace(/[^0-9.]/g, '');     // Remove non-numeric except decimal

    // Ensure only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      // Join all parts after first decimal point
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      sanitized = parts[0] + '.' + parts[1].substring(0, 2);
    }

    // Handle edge case where input starts with decimal point
    if (sanitized.startsWith('.')) {
      sanitized = '0' + sanitized;
    }

    // Prevent leading zeros (except for decimal cases)
    if (sanitized.length > 1 && sanitized.startsWith('0') && !sanitized.startsWith('0.')) {
      sanitized = sanitized.substring(1);
    }

    // Ensure result is a valid number string
    if (sanitized && isNaN(parseFloat(sanitized))) {
      return '';
    }

    return sanitized;
  }, []);

  // Cleanup timeout on unmount
  // Cleanup handled by UI store

  // Get explorer URL for transaction hash
  const getExplorerUrl = (network: string, txHash: string): string | null => {
    // Basic validation
    if (!txHash || typeof txHash !== 'string' || txHash.length < 10) {
      return null;
    }

    // Network-specific hash format validation
    const isValidHash = (hash: string, network: string): boolean => {
      switch (network) {
        case 'Ethereum':
        case 'Arbitrum':
        case 'Base':
        case 'Polygon':
        case 'BNB':
        case 'Avalanche':
          return /^0x[a-fA-F0-9]{64}$/.test(hash); // Ethereum-style: 0x + 64 hex chars
        // Solana support removed - EVM chains only
        case 'Bitcoin':
          return /^[a-fA-F0-9]{64}$/.test(hash); // Bitcoin: 64 hex chars
        default:
          return hash.length >= 10; // Fallback for unknown networks
      }
    };

    if (!isValidHash(txHash, network)) {
      return null;
    }

    const networkConfig = getNetworkConfig(network);
    if (!networkConfig?.blockExplorer) {
      // Fallback explorers for networks without config
      const fallbacks: Record<string, string> = {
        // Solana explorer removed - EVM chains only
        'Bitcoin': 'https://blockstream.info',
      };
      const fallbackUrl = fallbacks[network];
      return fallbackUrl ? `${fallbackUrl}/tx/${txHash}` : null;
    }

    return `${networkConfig.blockExplorer}/tx/${txHash}`;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (!isActive) {
    return (
      <div className="payment-widget inactive">
        <div className="widget-header">
          <h3>💳 Payment Widget</h3>
        </div>
        <div className="inactive-message">
          <span className="icon">⏸️</span>
          <p>Presale is currently inactive</p>
          <p>Please check back later</p>
        </div>
      </div>
    );
  }

  if (paymentSession && !paymentStatus) {
    return (
      <div className="payment-widget active-session">
        <div className="widget-header">
          <h3>💳 Complete Your Payment</h3>
          <button onClick={resetPayment} className="reset-button">
            ← Back
          </button>
        </div>

        <div className="payment-details">
          <div className="session-info">
            <div className="info-row">
              <span className="label">Network:</span>
              <span className="value">{paymentSession.network}</span>
            </div>
            <div className="info-row">
              <span className="label">Amount to Pay:</span>
              <span className="value">
                {formatCurrency(paymentSession.amount)}
                {paymentSession.tokenSymbol && ` (${paymentSession.tokenSymbol})`}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Tokens to Receive:</span>
              <span className="value">{paymentSession.tokensToReceive?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="info-row">
              <span className="label">Session Expires:</span>
              <span className="value">
                {new Date(paymentSession.expiresAt).toLocaleString()}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Time Remaining:</span>
              <span className={`value ${timeUntilExpiry < 300 ? 'expiring-soon' : ''} ${isSessionExpired ? 'expired' : ''}`}>
                {isSessionExpired ? 'EXPIRED' : formatTimeRemaining(timeUntilExpiry)}
              </span>
            </div>
          </div>

          {/* Payment Options */}
          <div className="payment-options">
            {/* Wallet Payment Status - automatically triggered */}
            {isWalletConnected() && (
              <div className="wallet-payment-section">
                <h4>💼 Wallet Payment</h4>
                <div className="wallet-status">
                  {txStatus === 'idle' && (
                    <p className="status-message">🔄 Preparing transaction...</p>
                  )}
                  {(txStatus === 'preparing' || txStatus === 'awaiting-signature') && (
                    <div className="status-indicator">
                      <div className="spinner-small"></div>
                      <p>Check your wallet to sign the transaction</p>
                    </div>
                  )}
                  {txStatus === 'broadcasting' && (
                    <div className="status-indicator">
                      <div className="spinner-small"></div>
                      <p>Broadcasting transaction...</p>
                    </div>
                  )}
                  {txStatus === 'confirming' && (
                    <div className="status-indicator">
                      <div className="spinner-small"></div>
                      <p>Confirming on blockchain...</p>
                    </div>
                  )}
                  {txStatus === 'success' && (
                    <div className="success-indicator">
                      <span className="success-icon">✅</span>
                      <p>Transaction sent successfully!</p>
                    </div>
                  )}
                  {txStatus === 'error' && (
                    <div className="error-indicator">
                      <span className="error-icon">⚠️</span>
                      <p>{txError}</p>
                      <button
                        onClick={() => {
                          setTxStatus('idle');
                          setTxError('');
                          setTxHash('');
                          handleSimpleWalletPayment();
                        }}
                        className="retry-button"
                      >
                        Retry Payment
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* QR Code - always visible */}
            <div className="qr-payment-section">
              <h4>📱 Alternative: Scan QR Code</h4>
              <button
                onClick={() => setShowQR(true)}
                className="qr-button"
                disabled={isSessionExpired}
              >
                Show QR Code
              </button>
            </div>
          </div>


          {/* Manual payment fallback */}
          {(!isWalletConnected() || ['Tron', 'Polkadot', 'Bitcoin'].includes(selectedNetwork)) && (
            <div className="manual-payment-section">
              <div className="payment-address">
                <label>Payment Address:</label>
                <div className="address-container">
                  <input
                    type="text"
                    value={paymentSession.paymentAddress}
                    readOnly
                    className="address-input"
                  />
                  <button 
                    onClick={() => copyToClipboard(paymentSession.paymentAddress)}
                    className="copy-button"
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="payment-instructions">
                <h4>📝 Payment Instructions:</h4>
                <ol>
                  <li>Send exactly <strong>{formatCurrency(paymentSession.amount)}</strong> to the address above</li>
                  <li>Use <strong>{paymentSession.network}</strong> network for payment</li>
                  {paymentSession.tokenSymbol && (
                    <li>Send <strong>{paymentSession.tokenSymbol}</strong> token{paymentSession.tokenAddress && ` (${paymentSession.tokenAddress})`}</li>
                  )}
                  <li>Payment will expire at the time shown above</li>
                  <li>You will receive <strong>{paymentSession.tokensToReceive.toFixed(2)} tokens</strong> after confirmation</li>
                </ol>
              </div>
            </div>
          )}

          {/* Polling status indicator */}
          {isPolling && txStatus !== 'success' && (
            <div className="status-indicator">
              <div className="spinner"></div>
              <p>Monitoring for payment confirmation...</p>
              <small>This page will update automatically when payment is received</small>
            </div>
          )}
        </div>

        {/* QR Code Modal */}
        {showQR && paymentSession && (
          <QRPayment
            network={paymentSession.network}
            address={paymentSession.paymentAddress}
            // Prefer base units for accurate prefills when available
            amount={paymentSession.amountBaseUnits ? undefined : paymentSession.amount?.toString()}
            amountBaseUnits={paymentSession.amountBaseUnits}
            chainId={paymentSession.chainId}
            tokenAddress={paymentSession.tokenAddress}
            tokenSymbol={paymentSession.tokenSymbol}
            decimals={paymentSession.decimals}
            onClose={() => setShowQR(false)}
          />
        )}
      </div>
    );
  }

  if (paymentStatus && ['completed', 'completed_success'].includes(paymentStatus.status)) {
    return (
      <div className="payment-widget completed">
        <div className="widget-header">
          <h3>✅ Payment Completed!</h3>
        </div>

          <div className="completion-details">
            <div className="success-message">
              <div className="success-icon">🎉</div>
              <h4>Payment Successful!</h4>
              <p>Your tokens have been allocated to your wallet</p>
              {copySuccess && (
                <div className="copy-feedback">
                  ✅ {copySuccess}
                </div>
              )}
            </div>

          <div className="payment-summary">
            <div className="summary-row">
              <span className="label">Amount Paid:</span>
              <span className="value">{formatCurrency(paymentStatus.amount_usd)}</span>
            </div>
            <div className="summary-row">
              <span className="label">Tokens Allocated:</span>
              <span className="value">{paymentStatus.tokens_allocated.toFixed(2)}</span>
            </div>
            {paymentStatus.tx_hash && (
              <div className="summary-row">
                <span className="label">Transaction Hash:</span>
                <div className="hash-container">
                  <span className="value hash">
                    {paymentStatus.tx_hash.substring(0, 10)}...{paymentStatus.tx_hash.substring(paymentStatus.tx_hash.length - 8)}
                  </span>
                  <div className="hash-actions">
                    <button
                      onClick={() => copyToClipboard(paymentStatus.tx_hash!, 'Transaction hash')}
                      className="copy-hash-btn"
                      title="Copy transaction hash"
                    >
                      📋
                    </button>
                    {getExplorerUrl(selectedNetwork, paymentStatus.tx_hash) && (
                      <a
                        href={getExplorerUrl(selectedNetwork, paymentStatus.tx_hash)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="explorer-link"
                        title="View on explorer"
                      >
                        🔗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="summary-row">
              <span className="label">Completed:</span>
              <span className="value">
                {paymentStatus.completed_at ? new Date(paymentStatus.completed_at).toLocaleString() : 'Just now'}
              </span>
            </div>
          </div>

          <button onClick={resetPayment} className="new-payment-button">
            Make Another Payment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-widget">
      <div className="widget-header">
        <h3>💳 Buy Tokens</h3>
        <div className="phase-info">
          <span className="phase-badge">Phase {currentPhase}</span>
          <span className="price-badge">{formatCurrency(pricePerToken)} per token</span>
        </div>
      </div>

      <div className="payment-form">
        {/* Network Selection */}
        <NetworkSelector
          selectedNetwork={selectedNetwork}
          onNetworkChange={(network) => {
            const networkValidation = validateNetwork(network);
            if (!networkValidation.isValid) {
              setError(networkValidation.error || 'Invalid network');
              return;
            }
            setNetwork(network);
            setToken(undefined); // Reset token when network changes
            setError(null);
          }}
          disabled={loading}
        />

        {/* Payment Method Selection */}
        <PaymentMethodSelector
          network={selectedNetwork}
          selectedToken={selectedToken}
          onTokenChange={(token) => {
            if (token) {
              const tokenValidation = validateToken(token, selectedNetwork);
              if (!tokenValidation.isValid) {
                setError(tokenValidation.error || 'Invalid token');
                return;
              }
            }
            setToken(token);
            setError(null);
          }}
          pricePerToken={pricePerToken}
          amount={parseFloat(amountUsd || '0')}
          disabled={loading}
        />

        <div className="amount-input-group">
          <label htmlFor="amount">Amount (USD)</label>
          <div className="input-container">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="amount"
              value={amountUsd}
              onChange={(e) => {
                const sanitized = sanitizeAmountInput(e.target.value);
                setAmount(sanitized);

                // Clear errors when user starts typing valid input
                if (error && error.includes('amount')) {
                  setError(null);
                }
              }}
              placeholder="10.00"
              min="1"
              step="0.01"
              className="amount-input"
            />
          </div>
        </div>

        <div className="tokens-preview">
          <div className="preview-row">
            <span className="label">Tokens to receive:</span>
            <span className="value">{tokensToReceive.toFixed(2)}</span>
          </div>
          <div className="preview-row">
            <span className="label">Price per token:</span>
            <span className="value">{formatCurrency(pricePerToken)}</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <button
          onClick={handleBuyNow}
          disabled={loading || !getIsValidAmount() || isRateLimited}
          className="buy-now-button"
        >
          {loading ? (
            <>
              <div className="spinner-small"></div>
              {isWalletConnected() ? 'Creating Payment...' : 'Connecting...'}
            </>
          ) : isRateLimited ? (
            'Please wait...'
          ) : (
            <>
              Buy Now
              {isWalletConnected() && supportsDirectWalletPayment(selectedNetwork, selectedToken) && (
                <span className="wallet-payment-badge">💳</span>
              )}
            </>
          )}
        </button>

        <div className="wallet-info">
          <small>
            <strong>Connected Wallet:</strong> {walletAddress.substring(0, 6)}...{walletAddress.substring(walletAddress.length - 4)}
          </small>
        </div>
      </div>
      
      <style jsx="true">{`
        .payment-methods {
          margin: 1.5rem 0;
          padding: 1.5rem;
          background: var(--bg-secondary, #f9fafb);
          border-radius: 0.75rem;
          border: 1px solid var(--border-color, #e5e7eb);
        }

        .method-message {
          font-size: 0.875rem;
          color: var(--text-secondary, #666);
          margin-bottom: 1rem;
          text-align: center;
        }

        .method-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 640px) {
          .method-buttons {
            grid-template-columns: 1fr;
          }
        }

        .method-button {
          position: relative;
          padding: 1rem 1.5rem;
          border: none;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .method-button.primary {
          background: var(--accent-color, #6366f1);
          color: white;
        }

        .method-button.primary:hover:not(:disabled) {
          background: var(--accent-dark, #4f46e5);
        }

        .method-button.secondary {
          background: var(--bg-primary, #fff);
          color: var(--text-primary, #111);
          border: 2px solid var(--border-color, #e5e7eb);
        }

        .method-button.secondary:hover:not(:disabled) {
          border-color: var(--accent-color, #6366f1);
          background: var(--accent-light, #f3f4ff);
        }

        .method-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .recommended-badge {
          position: absolute;
          top: -0.5rem;
          right: -0.5rem;
          background: var(--success-color, #10b981);
          color: white;
          font-size: 0.625rem;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
        }

        .method-button.recommended {
          border-color: var(--success-color, #10b981);
        }

        .manual-payment-section {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border-color, #e5e7eb);
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .expiring-soon {
          color: var(--warning-color, #f59e0b) !important;
          font-weight: bold;
          animation: pulse 2s infinite;
        }

        .expired {
          color: var(--error-color, #dc2626) !important;
          font-weight: bold;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .payment-options {
          margin: 1.5rem 0;
          padding: 1.5rem;
          background: var(--bg-secondary, #f9fafb);
          border-radius: 0.75rem;
          border: 1px solid var(--border-color, #e5e7eb);
        }

        .wallet-payment-section, .qr-payment-section {
          margin-bottom: 1.5rem;
        }

        .wallet-payment-section:last-child,
        .qr-payment-section:last-child {
          margin-bottom: 0;
        }

        .wallet-payment-section h4, .qr-payment-section h4 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: var(--text-primary, #111);
        }

        .wallet-status {
          background: white;
          padding: 1rem;
          border-radius: 0.5rem;
          border: 1px solid var(--border-color, #e5e7eb);
        }

        .status-message {
          color: var(--text-secondary, #666);
          font-size: 0.875rem;
          margin: 0;
        }

        .status-indicator, .success-indicator, .error-indicator {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .status-indicator p, .success-indicator p, .error-indicator p {
          margin: 0;
          font-size: 0.875rem;
        }

        .success-indicator {
          color: var(--success-color, #10b981);
        }

        .success-icon {
          font-size: 1.25rem;
        }

        .error-indicator {
          color: var(--error-color, #dc2626);
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }

        .error-icon {
          font-size: 1.25rem;
        }

        .retry-button {
          background: var(--accent-color, #6366f1);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .retry-button:hover {
          background: var(--accent-dark, #4f46e5);
        }

        .qr-button {
          background: var(--bg-primary, #fff);
          color: var(--text-primary, #111);
          border: 2px solid var(--border-color, #e5e7eb);
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .qr-button:hover:not(:disabled) {
          border-color: var(--accent-color, #6366f1);
          background: var(--accent-light, #f3f4ff);
        }

        .qr-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .buy-now-button {
          background: linear-gradient(135deg, var(--accent-color, #6366f1), var(--accent-dark, #4f46e5));
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 0.75rem;
          font-size: 1.125rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
          width: 100%;
          max-width: 300px;
          margin: 1rem auto 0;
          display: block;
        }

        .buy-now-button:hover:not(:disabled) {
          background: linear-gradient(135deg, var(--accent-dark, #4f46e5), #3730a3);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
        }

        .buy-now-button:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
        }

        .buy-now-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .wallet-payment-badge {
          margin-left: 0.5rem;
          opacity: 0.8;
          font-size: 1rem;
        }

        .hash-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
        }

        .hash-actions {
          display: flex;
          gap: 0.25rem;
        }

        .copy-hash-btn, .explorer-link {
          background: var(--bg-secondary, #f3f4f6);
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 0.25rem;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          color: var(--text-primary, #111);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .copy-hash-btn:hover, .explorer-link:hover {
          background: var(--accent-light, #f3f4ff);
          border-color: var(--accent-color, #6366f1);
        }

        .copy-feedback {
          margin-top: 1rem;
          padding: 0.75rem;
          background: var(--success-light, #f0fdf4);
          color: var(--success-color, #10b981);
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          border: 1px solid var(--success-border, #bbf7d0);
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentWidget;
